import React, { FC, useState } from 'react';
import { Card, ColumnLayout, FlexGroup, FlexItem } from '@lithos';
import {
  calculateStep,
  calculateUnit,
  DateRangeUnitEnum,
  MonitoringDateRange,
} from '../../monitoring/DateRange-Dropdown-util';
import PrometheusMonitoring from '../../monitoring/PrometheusMonitoring';
import { useParams } from 'react-router-dom';
import { TimeSelector } from '@tecton';
import moment from 'moment';
import { formatBytesPerSecond } from './utils';

const DEFAULT_TIME_RANGE = {
  startTime: () => moment().subtract(1, 'hour').toDate().getTime(),
  endTime: () => moment().toDate().getTime(),
  step: 600,
  requestId: new Date().getTime().toString(),
  unit: DateRangeUnitEnum.minute,
};

const TransformServerGroupCharts: FC<{ monitoringDateRange: MonitoringDateRange; id: string }> = ({
  monitoringDateRange,
  id,
}) => {
  return (
    <ColumnLayout>
      <Card title="Request Rate">
        <PrometheusMonitoring
          pluginLegendOverride={{ display: false }}
          chartTitle={''}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Evaluate',
              query: `sum by (grpc_method) (rate(grpc_server_handled_total{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m]))`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Requests Per Second'}
          yTicksCallback={undefined}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y + ' requests/s';
          }}
        />
      </Card>

      <Card title="Response Percentage By Code">
        <PrometheusMonitoring
          chartTitle={''}
          pluginLegendOverride={{ display: false }}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Response Percentage by gRPC Status Code',
              query: `(sum(rate(grpc_server_handled_total{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m])) by (grpc_code) / ignoring(grpc_code) group_left sum(rate(grpc_server_handled_total{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m]))) * 100`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'% by Status Code'}
          yTicksCallback={(value: any) => {
            return value + '%';
          }}
          yGrace={''}
          getMetricLabelCallback={(arg1: any, label?: string | undefined) => {
            return arg1.metric.grpc_code.toString() ?? '';
          }}
          toolTipLabelCallback={(value: any, a: any) => {
            return value.dataset.label + ': ' + value.raw.y + '%';
          }}
        />
      </Card>

      <Card title="Request Latency">
        <PrometheusMonitoring
          chartTitle={''}
          pluginLegendOverride={{ display: false }}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'p99',
              query: `histogram_quantile(0.99, sum(rate(grpc_server_handling_seconds_bucket{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
            {
              label: 'p95',
              query: `histogram_quantile(0.95, sum(rate(grpc_server_handling_seconds_bucket{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
            {
              label: 'p90',
              query: `histogram_quantile(0.9, sum(rate(grpc_server_handling_seconds_bucket{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
            {
              label: 'p50',
              query: `histogram_quantile(0.5, sum(rate(grpc_server_handling_seconds_bucket{ aws_region=~'(us-east-1|us-west-2)', transform_server_group=~'tecton-tsg-${id}', grpc_method='Evaluate', tecton_cluster=~'.*'}[5m])) by (le))`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Latency (ms)'}
          yTicksCallback={(value: any) => {
            return value + 'ms';
          }}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y.toFixed(2) + 'ms';
          }}
        />
      </Card>

      <Card title="Server Group Capacity">
        <PrometheusMonitoring
          chartTitle={''}
          pluginLegendOverride={{ display: false }}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Desired',
              query: `aws_autoscaling_group_desired_capacity_average{
auto_scaling_group_name=~"tecton-tsg-${id}",
tecton_cluster=~".*"
}`,
            },
            {
              label: 'Min',
              query: `aws_autoscaling_group_min_size_average{
auto_scaling_group_name=~"tecton-tsg-${id}",
tecton_cluster=~".*"
}`,
            },
            {
              label: 'Max',
              query: `aws_autoscaling_group_max_size_average{
auto_scaling_group_name=~"tecton-tsg-${id}",
tecton_cluster=~".*"
}`,
            },
            {
              label: 'Current',
              query: `aws_autoscaling_group_in_service_instances_average{
auto_scaling_group_name=~"tecton-tsg-${id}",
tecton_cluster=~".*"
}`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Node Count'}
          yTicksCallback={undefined}
          yGrace={''}
          toolTipLabelCallback={undefined}
          parseValue={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100;
          }}
        />
      </Card>
      <Card title="CPU Utilization">
        <PrometheusMonitoring
          chartTitle={''}
          pluginLegendOverride={{ display: false }}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Max',
              query: `max(100 - (irate(node_cpu_seconds_total{tecton_cluster=~".*",transform_server_group=~'tecton-tsg-${id}',job="transform_server_group",mode="idle"}[5m]) * 100))`,
            },
            {
              label: 'Average',
              query: `avg(100 - (irate(node_cpu_seconds_total{tecton_cluster=~".*",transform_server_group=~'tecton-tsg-${id}',job="transform_server_group",mode="idle"}[5m]) * 100))`,
            },
            {
              label: 'Min',
              query: `min(100 - (irate(node_cpu_seconds_total{tecton_cluster=~".*",transform_server_group=~'tecton-tsg-${id}',job="transform_server_group",mode="idle"}[5m]) * 100))`,
            },
          ]}
          xAxisLabel={''}
          yAxisLabel={'Utilization (%)'}
          yTicksCallback={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100 + '%';
          }}
          yGrace={''}
          parseValue={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100;
          }}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y + '%';
          }}
        />
      </Card>

      <Card title="Memory Utilization">
        <PrometheusMonitoring
          chartTitle={''}
          pluginLegendOverride={{ display: false }}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Max',
              query: `max((1 - (node_memory_MemAvailable_bytes{tecton_cluster=~".*",transform_server_group=~'tecton-tsg-${id}',job="transform_server_group"} / node_memory_MemTotal_bytes{tecton_cluster=~".*",transform_server_group=~"tecton-tsg-${id}",job="transform_server_group"})) * 100)`,
            },
            {
              label: 'Average',
              query: `avg((1 - (node_memory_MemAvailable_bytes{tecton_cluster=~".*",transform_server_group=~'tecton-tsg-${id}',job="transform_server_group"} / node_memory_MemTotal_bytes{tecton_cluster=~".*",transform_server_group=~"tecton-tsg-${id}",job="transform_server_group"})) * 100)`,
            },
            {
              label: 'Min',
              query: `min((1 - (node_memory_MemAvailable_bytes{tecton_cluster=~".*",transform_server_group=~'tecton-tsg-${id}',job="transform_server_group"} / node_memory_MemTotal_bytes{tecton_cluster=~".*",transform_server_group=~"tecton-tsg-${id}",job="transform_server_group"})) * 100)`,
            },
          ]}
          xAxisLabel={'Time'}
          yAxisLabel={'Utilization (%)'}
          yTicksCallback={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100 + '%';
          }}
          yGrace={''}
          parseValue={(value: any) => {
            return Math.round(parseFloat(value) * 100) / 100;
          }}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + value.raw.y + '%';
          }}
        />
      </Card>
      <Card title="Network Inbound / Outbound Throughput">
        <PrometheusMonitoring
          chartTitle={''}
          pluginLegendOverride={{ display: false }}
          monitoringDateRange={monitoringDateRange}
          queries={[
            {
              label: 'Receive',
              query: `sum by (transform_server_group) (
rate(node_network_receive_bytes_total{
job="transform_server_group",
tecton_cluster=~".*",
transform_server_group=~"tecton-tsg-${id}",
device!~"^lo|docker|veth|br|virbr|cni|flannel"
}[5m])
)`,
            },
            {
              label: 'Transmit',
              query: `sum by (transform_server_group) (
rate(node_network_transmit_bytes_total{
job="transform_server_group",
tecton_cluster=~".*",
transform_server_group=~"tecton-tsg-${id}",
device!~"^lo|docker|veth|br|virbr|cni|flannel"
}[5m])
)`,
            },
          ]}
          xAxisLabel={'Time'}
          yAxisLabel={'Throughput'}
          yTicksCallback={(value: any) => {
            return formatBytesPerSecond(value);
          }}
          yGrace={''}
          toolTipLabelCallback={(value: any) => {
            return value.dataset.label + ': ' + formatBytesPerSecond(value.raw.y);
          }}
        />
      </Card>
    </ColumnLayout>
  );
};

const TransformServerGroupMonitoring: FC = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<MonitoringDateRange>(DEFAULT_TIME_RANGE);
  const { id } = useParams();

  return (
    <FlexGroup direction="column" gapSize="4">
      <FlexItem grow="1" shrink="0">
        <TimeSelector
          onSelection={(start, end) => {
            setSelectedTimeRange({
              startTime: () => start.valueOf(),
              endTime: () => end.valueOf(),
              step: calculateStep(moment(start), moment(end)),
              requestId: new Date().getTime().toString(),
              unit: calculateUnit(moment(start), moment(end)),
            });
          }}
        />
      </FlexItem>
      <FlexItem>
        <TransformServerGroupCharts monitoringDateRange={selectedTimeRange} id={id ?? ''} />
      </FlexItem>
    </FlexGroup>
  );
};

export default TransformServerGroupMonitoring;
